import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { COUNTRIES, countryName } from '../schemas/countries';

export interface CountrySelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  /** Countries served by another market of the store, keyed to that market's name; shown but not selectable. */
  taken?: ReadonlyMap<string, string>;
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
}

/** Multi-select of ISO countries: chips for the chosen ones, a searchable list to add more. */
export function CountrySelect({
  value,
  onChange,
  onBlur,
  taken,
  disabled,
  id,
  'aria-invalid': ariaInvalid,
}: CountrySelectProps) {
  const { t } = useTranslation('markets');
  const [open, setOpen] = useState(false);
  const selected = new Set(value);

  const toggle = (code: string) => {
    onChange(selected.has(code) ? value.filter((c) => c !== code) : [...value, code]);
  };

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('fields.selectedCountries')}>
          {value.map((code) => (
            <li key={code}>
              <Badge variant="secondary" className="gap-1 pr-1">
                {countryName(code)}
                <button
                  type="button"
                  className="hover:bg-foreground/10 rounded-full p-0.5"
                  aria-label={t('fields.removeCountry', { country: countryName(code) })}
                  onClick={() => toggle(code)}
                  disabled={disabled}
                >
                  <X className="size-3" aria-hidden />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) onBlur?.();
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={ariaInvalid}
            disabled={disabled}
            className="text-muted-foreground w-full justify-between font-normal"
          >
            {t('fields.addCountries')}
            <ChevronsUpDown className="size-4 opacity-60" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('fields.searchCountries')} />
            <CommandList>
              <CommandEmpty>{t('fields.noCountries')}</CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map((country) => {
                  const isSelected = selected.has(country.code);
                  const takenBy = taken?.get(country.code);
                  return (
                    <CommandItem
                      key={country.code}
                      value={`${country.name} ${country.code}`}
                      disabled={Boolean(takenBy)}
                      onSelect={() => toggle(country.code)}
                    >
                      <Check
                        className={cn('size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                        aria-hidden
                      />
                      <span className="flex-1">{country.name}</span>
                      <span className="text-muted-foreground font-mono text-[11px]">
                        {takenBy ? t('fields.takenBy', { market: takenBy }) : country.code}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
